import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Sidebar from '../Dashboard/Sidebar';
import MetaData from '../layouts/MetaData';
import Loader from '../layouts/Loader';
import DeleteConfirmModal from '../layouts/DeleteConfirmModal';
import { getCategories, createCategory, updateCategory, deleteCategory } from '../../actions/categoryActions';
import { clearCategoryCreated, clearCategoryUpdated, clearCategoryDeleted, clearCategoryError } from '../../slices/categorySlice';

export default function CategoryList() {
    const dispatch = useDispatch();
    const { 
        categories = [], 
        loading, 
        isCategoryCreated, 
        isCreated, 
        isCategoryUpdated, 
        isUpdated, 
        isCategoryDeleted, 
        isDeleted, 
        error 
    } = useSelector(state => state.categoryState);
    const { user } = useSelector(state => state.authState);
    const isAdmin = user?.role === 'admin' || user?.role === 'master';

    const [newType, setNewType] = useState('income');
    const [newName, setNewName] = useState('');
    const [editId, setEditId] = useState(null);
    const [editName, setEditName] = useState('');

    useEffect(() => {
        dispatch(clearCategoryCreated());
        dispatch(clearCategoryUpdated());
        dispatch(clearCategoryDeleted());
        dispatch(clearCategoryError());
        dispatch(getCategories());
        return () => {
            dispatch(clearCategoryCreated());
            dispatch(clearCategoryUpdated());
            dispatch(clearCategoryDeleted());
            dispatch(clearCategoryError());
        };
    }, [dispatch]);

    useEffect(() => {
        if (isCategoryCreated || isCreated) {
            toast('Category created successfully', { type: 'success' });
            dispatch(clearCategoryCreated());
            setNewName('');
            dispatch(getCategories());
        }
        if (isCategoryUpdated || isUpdated) {
            toast('Category updated successfully', { type: 'success' });
            dispatch(clearCategoryUpdated());
            setEditId(null);
            dispatch(getCategories());
        }
        if (isCategoryDeleted || isDeleted) {
            toast('Category deleted successfully', { type: 'success' });
            dispatch(clearCategoryDeleted());
            dispatch(getCategories());
        }
        if (error) {
            toast(error, { type: 'error' });
            dispatch(clearCategoryError());
        }
    }, [isCategoryCreated, isCreated, isCategoryUpdated, isUpdated, isCategoryDeleted, isDeleted, error, dispatch]);

    const handleAdd = (e) => {
        e.preventDefault();
        if (!newName.trim()) { toast('Please enter a category name', { type: 'error' }); return; }
        dispatch(createCategory({ name: newName.trim(), type: newType }));
    };

    const startEdit = (c) => {
        setEditId(c._id || c.id);
        setEditName(c.name);
    };

    const handleUpdate = (id) => {
        if (!editName.trim()) { toast('Name cannot be empty', { type: 'error' }); return; }
        dispatch(updateCategory(id, { name: editName.trim() }));
    };

    const [deleteModal, setDeleteModal] = useState({ show: false, id: null, name: '' });

    const handleDelete = (id, name, type) => {
        setDeleteModal({ show: true, id, name });
    };

    const confirmDelete = () => {
        if (deleteModal.id) {
            dispatch(deleteCategory(deleteModal.id));
        }
        setDeleteModal({ show: false, id: null, name: '' });
    };

    const btnStyle = (bg, color, border) => ({
        background: bg, color, border, borderRadius: '6px', fontSize: '0.8rem', padding: '0.35rem 0.6rem'
    });

    return (
        <div className="row g-0">
            <div className="col-12 col-md-2">
                <Sidebar />
            </div>
            <div className="col-12 col-md-10 main-content">
                <MetaData title="Account Categories" />

                <div className="p-4" style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-between align-items-end mb-4">
                        <div>
                            <h2 className="mb-1" style={{ fontWeight: 700, color: 'var(--text-dark)', letterSpacing: '-0.5px' }}>
                                Account Categories
                            </h2>
                            <p className="mb-0 text-muted" style={{ fontSize: '0.9rem' }}>
                                Manage income and expense categories.
                            </p>
                        </div>
                    </div>

                    <div className="row g-4">
                        {isAdmin && (
                            <div className="col-12 col-xl-4 order-xl-2">
                                <div className="card shadow-sm border-0" style={{ borderRadius: '12px' }}>
                                    <div className="card-header bg-white border-bottom-0 pt-4 pb-0 px-4">
                                        <h6 className="mb-0 fw-bold" style={{ color: 'var(--primary)' }}>
                                            <i className="fa fa-plus-circle me-2"></i>Add New Category
                                        </h6>
                                    </div>
                                    <div className="card-body p-4">
                                        <form onSubmit={handleAdd}>
                                            <div className="mb-3">
                                                <label className="form-label" style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-muted)' }}>Type</label>
                                                <select
                                                    className="form-control"
                                                    value={newType}
                                                    onChange={(e) => setNewType(e.target.value)}
                                                >
                                                    <option value="income">Income</option>
                                                    <option value="expense">Expense</option>
                                                </select>
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label" style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-muted)' }}>Name</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="e.g. Salary, Rent"
                                                    value={newName}
                                                    onChange={(e) => setNewName(e.target.value)}
                                                    required
                                                />
                                            </div>
                                            <button
                                                type="submit"
                                                className="btn btn-primary w-100 mt-2 py-2 fw-semibold"
                                                disabled={loading}
                                            >
                                                {loading ? 'Adding...' : 'Add Category'}
                                            </button>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className={`col-12 ${isAdmin ? 'col-xl-8 order-xl-1' : 'col-xl-12'}`}>
                            <div className="card shadow-sm border-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                                {loading && !categories.length ? (
                                    <Loader />
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-hover align-middle mb-0" style={{ fontSize: '0.9rem' }}>
                                            <thead style={{ backgroundColor: '#f1f5f9' }}>
                                                <tr>
                                                    <th className="px-4 py-3 text-muted fw-semibold" style={{ width: '20%' }}>Type</th>
                                                    <th className="px-4 py-3 text-muted fw-semibold" style={{ width: '40%' }}>Category Name</th>
                                                    {isAdmin && <th className="px-4 py-3 text-end text-muted fw-semibold">Actions</th>}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {categories.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={isAdmin ? 3 : 2} className="text-center py-5 text-muted">
                                                            No categories found.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    categories.map((c) => {
                                                        const catId = c._id || c.id;
                                                        return (
                                                            <tr key={catId}>
                                                                <td className="px-4 py-3">
                                                                    <span className={`badge ${c.type === 'income' ? 'bg-success' : 'bg-danger'}`}>
                                                                        {c.type === 'income' ? 'Income' : 'Expense'}
                                                                    </span>
                                                                </td>
                                                                {editId === catId ? (
                                                                    <>
                                                                        <td className="px-4 py-3">
                                                                            <input
                                                                                className="form-control form-control-sm"
                                                                                value={editName}
                                                                                onChange={(e) => setEditName(e.target.value)}
                                                                                autoFocus
                                                                            />
                                                                        </td>
                                                                        {isAdmin && (
                                                                            <td className="px-4 py-3 text-end">
                                                                                <button
                                                                                    className="btn btn-sm me-1"
                                                                                    style={btnStyle('rgba(16,185,129,0.1)', '#065f46', '1px solid rgba(16,185,129,0.3)')}
                                                                                    onClick={() => handleUpdate(catId)}
                                                                                    disabled={loading}
                                                                                >
                                                                                    <i className="fa fa-check me-1"></i>Save
                                                                                </button>
                                                                                <button
                                                                                    className="btn btn-sm"
                                                                                    style={btnStyle('rgba(100,116,139,0.1)', '#475569', '1px solid rgba(100,116,139,0.3)')}
                                                                                    onClick={() => setEditId(null)}
                                                                                >
                                                                                    Cancel
                                                                                </button>
                                                                            </td>
                                                                        )}
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <td className="px-4 py-3 fw-medium" style={{ color: 'var(--text-dark)' }}>
                                                                            {c.name}
                                                                        </td>
                                                                        {isAdmin && (
                                                                            <td className="px-4 py-3 text-end">
                                                                                <button
                                                                                    className="btn btn-sm me-1"
                                                                                    style={btnStyle('rgba(14,165,233,0.1)', '#0369a1', '1px solid rgba(14,165,233,0.3)')}
                                                                                    onClick={() => startEdit(c)}
                                                                                    title="Edit Category"
                                                                                >
                                                                                    <i className="fa fa-pencil"></i>
                                                                                </button>
                                                                                <button
                                                                                    className="btn btn-sm"
                                                                                    style={btnStyle('rgba(239,68,68,0.1)', '#b91c1c', '1px solid rgba(239,68,68,0.3)')}
                                                                                    onClick={() => handleDelete(catId, c.name, c.type)}
                                                                                    title="Delete Category"
                                                                                >
                                                                                    <i className="fa fa-trash"></i>
                                                                                </button>
                                                                            </td>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </tr>
                                                        );
                                                    })
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <DeleteConfirmModal
                    show={deleteModal.show}
                    itemName={deleteModal.name}
                    onConfirm={confirmDelete}
                    onClose={() => setDeleteModal({ show: false, id: null, name: '' })}
                />
            </div>
        </div>
    );
}
